// Service for VS Code integration
const openFileInEditor = async (filePath: string, lineNumber?: number) => {
  try {
    const response = await fetch('http://localhost:3001/api/editor/open-file', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filePath, lineNumber }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to open file');
    }
    
    return true;
  } catch (error) {
    console.error('Error opening file:', error);
    return false;
  }
};

// Props history management for undo/redo
class PropsHistory {
  private history: Array<Record<string, any>> = [];
  private currentIndex: number = -1;

  push(props: Record<string, any>) {
    // Remove any future history if we're not at the end
    this.history = this.history.slice(0, this.currentIndex + 1);
    this.history.push(JSON.parse(JSON.stringify(props)));
    this.currentIndex++;
  }

  undo(): Record<string, any> | null {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      return JSON.parse(JSON.stringify(this.history[this.currentIndex]));
    }
    return null;
  }

  redo(): Record<string, any> | null {
    if (this.currentIndex < this.history.length - 1) {
      this.currentIndex++;
      return JSON.parse(JSON.stringify(this.history[this.currentIndex]));
    }
    return null;
  }

  canUndo(): boolean {
    return this.currentIndex > 0;
  }

  canRedo(): boolean {
    return this.currentIndex < this.history.length - 1;
  }

  clear() {
    this.history = [];
    this.currentIndex = -1;
  }
}

// Preview management
interface PreviewState {
  original: Record<string, any>;
  modified: Record<string, any>;
  element: HTMLElement | null;
}

class PreviewManager {
  private state: PreviewState | null = null;
  private originalStyles: Partial<CSSStyleDeclaration> | null = null;

  startPreview(element: HTMLElement, originalProps: Record<string, any>, modifiedProps: Record<string, any>) {
    this.state = {
      original: originalProps,
      modified: modifiedProps,
      element
    };
    
    // Store original styles
    this.originalStyles = {
      transition: element.style.transition,
      outline: element.style.outline,
      outlineOffset: element.style.outlineOffset
    };

    // Apply preview styles
    element.style.transition = 'all 0.3s ease-in-out';
    element.style.outline = '2px solid #3b82f6';
    element.style.outlineOffset = '2px';

    // Apply modified props
    this.applyPreviewProps(modifiedProps);
  }

  applyPreviewProps(props: Record<string, any>) {
    if (!this.state?.element) return;

    // Apply className changes
    if (props.className) {
      this.state.element.className = props.className;
    }

    // Apply style changes
    if (props.style) {
      Object.assign(this.state.element.style, props.style);
    }

    // Apply other applicable props
    if (props.disabled !== undefined) {
      this.state.element.toggleAttribute('disabled', props.disabled);
    }
    if (props.value !== undefined) {
      (this.state.element as any).value = props.value;
    }
    if (props.placeholder !== undefined) {
      this.state.element.setAttribute('placeholder', props.placeholder);
    }
  }

  stopPreview() {
    if (this.state?.element && this.originalStyles) {
      // Restore original styles
      Object.assign(this.state.element.style, this.originalStyles);
      
      // Restore original props
      this.applyPreviewProps(this.state.original);
    }
    
    this.state = null;
    this.originalStyles = null;
  }

  isPreviewing(): boolean {
    return this.state !== null;
  }
}

export { openFileInEditor, PropsHistory, PreviewManager };